import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from '@rocco/components/select';
import { cn } from '@rocco/utils/cn';
import { GATE_FIELD_INPUT_CLASS } from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import type { BookingRosterMember } from '../api/get-request-availability.server';

/** The practice itself. Radix needs a non-empty value, and null is not one. */
const ANY_VALUE = '__any__';

interface BookMeetingPersonPickerProps {
    id?: string;
    roster: BookingRosterMember[];
    selectedTeamMemberId: number | null;
    onSelect: (teamMemberId: number | null) => void;
    /** Named on the "anyone" row, which is a request of the practice. */
    professionalName: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
}

interface RowFace {
    image?: string | null;
    name: string;
    /** A connected calendar, so this person can be booked on the spot. */
    isLive: boolean;
    /**
     * A logo rather than a photograph, so it sits whole on the practice's own
     * fill. Cropping one to a circle cuts the wordmark in half.
     */
    isLogo?: boolean;
    background?: string | null;
}

/**
 * Avatar, with the green dot that means a time picked here is booked rather
 * than requested.
 *
 * The dot is the same one the "available now" card uses, at the same corner:
 * a green disc on a white ring, so it reads on a photograph of any brightness.
 */
const Face = ({
    image,
    name,
    isLive,
    isLogo,
    background,
    size,
}: RowFace & { size: string }) => (
    <span className={cn('relative shrink-0', size)}>
        {image ? (
            <img
                src={image}
                alt=""
                className={cn(
                    'size-full rounded-full',
                    isLogo
                        ? 'border border-black/8 bg-white object-contain p-1'
                        : 'object-cover',
                )}
                style={
                    isLogo && background
                        ? { backgroundColor: background }
                        : undefined
                }
            />
        ) : (
            <span
                className="flex size-full items-center justify-center rounded-full bg-black/8 text-[0.75rem] font-medium text-black/45"
                aria-hidden
            >
                {name.trim().charAt(0).toUpperCase() || '?'}
            </span>
        )}
        {isLive ? (
            <span
                className="absolute right-0 bottom-0 block size-[30%] rounded-full bg-[#22c55e] ring-2 ring-white"
                // The row already says the name; this is the one thing on it a
                // screen reader cannot get from the text.
                aria-label="Books instantly"
                role="img"
            />
        ) : null}
    </span>
);

/**
 * Who the reader would like to meet.
 *
 * A plain select showed the roster as a column of names, and a roster is mostly
 * the same name until the role is next to it: "Unnamed Team Member" nine times
 * is not a choice. Each row carries the face, the name and the job title, and
 * the people whose calendars are connected are marked, because picking one of
 * them books the time outright where picking anyone else asks for it.
 */
export const BookMeetingPersonPicker = ({
    id,
    roster,
    selectedTeamMemberId,
    onSelect,
    professionalName,
    companyLogo,
    logoBackgroundColor,
}: BookMeetingPersonPickerProps) => {
    const selected =
        roster.find((member) => member.teamMemberId === selectedTeamMemberId) ??
        null;

    const anyFace: RowFace = {
        image: companyLogo,
        name: professionalName,
        isLive: false,
        isLogo: true,
        background: logoBackgroundColor,
    };
    const triggerFace: RowFace = selected
        ? {
              image: selected.image,
              name: selected.name,
              isLive: selected.sessionId !== null,
          }
        : anyFace;

    return (
        <Select
            // Read off `selected`, the same thing the trigger draws from, so
            // the control cannot say "Anyone in the team" while still carrying
            // a member id. An id the roster does not have resolves to the
            // practice in both halves rather than to a value no row matches.
            value={selected ? String(selected.teamMemberId) : ANY_VALUE}
            onValueChange={(next) =>
                onSelect(next === ANY_VALUE ? null : Number(next))
            }
        >
            {/* The gate's own field shape rather than the trigger's default
                variants, so this sits in the same column as the date field. */}
            <SelectTrigger
                id={id}
                variant="small"
                color="transparent"
                className={cn(
                    GATE_FIELD_INPUT_CLASS,
                    'h-13 w-full justify-between gap-2 px-2.5',
                )}
            >
                <span className="flex min-w-0 items-center gap-2.5">
                    <Face {...triggerFace} size="size-8" />
                    <span className="truncate text-[1rem] leading-none text-[#161616]">
                        {selected ? selected.name : 'Anyone in the team'}
                    </span>
                </span>
            </SelectTrigger>

            <SelectContent className="max-h-80">
                <SelectItem
                    value={ANY_VALUE}
                    textValue="Anyone in the team"
                    hideIndicator
                    className="py-2"
                >
                    <Row
                        face={anyFace}
                        title="Anyone in the team"
                        subtitle={`Whoever at ${professionalName} is free`}
                    />
                </SelectItem>

                {roster.map((member) => (
                    <SelectItem
                        key={member.teamMemberId}
                        value={String(member.teamMemberId)}
                        textValue={member.name}
                        hideIndicator
                        className="py-2"
                    >
                        <Row
                            face={{
                                image: member.image,
                                name: member.name,
                                isLive: member.sessionId !== null,
                            }}
                            title={member.name}
                            subtitle={
                                member.jobTitle ||
                                (member.sessionId !== null
                                    ? 'Books instantly'
                                    : '')
                            }
                        />
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

/** The name over the role, with the face beside both. */
const Row = ({
    face,
    title,
    subtitle,
}: {
    face: RowFace;
    title: string;
    subtitle: string;
}) => (
    <span className="flex min-w-0 items-center gap-2.5">
        <Face {...face} size="size-9" />
        <span className="flex min-w-0 flex-col">
            <span className="truncate text-[0.9375rem] leading-[1.25] text-[#161616]">
                {title}
            </span>
            {subtitle ? (
                <span className="truncate text-[0.8125rem] leading-[1.3] text-black/50">
                    {subtitle}
                </span>
            ) : null}
        </span>
    </span>
);
